import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useQueryParams, StringParam } from "use-query-params";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Divider,
  Section,
  Icon,
  ButtonGroup,
  Pagination,
  LinearProgress,
  Typography,
} from "~/components/UI";
import ListLayoutSwitcher, {
  LayoutType,
  eLayoutType,
} from "~/components/ListLayoutSwitcher";
import Table from "./Table";
import Tiles from "./Tiles";
import UpdateEvPortDialog from "./UpdateEvPortDialog";
import EvPortsContext from "./evPortsContext";
import { updateArrayItem, getSortStr } from "~/utils/helpers";
import useConfirmDialog from "~/hook/useConfirmDialog";
import { PERMISSION } from "~/api/permissions/enums";
import CanIUse from "~/components/CanIUse";
import RowsPerPage from "~/components/RowsPerPage";
import api from "~/api";

interface ListProps {
  type: LayoutType;
  data?: Api.EvPortDto[];
}

const List = React.memo(({ type, data }: ListProps) => {
  return type === eLayoutType.TABLE ? (
    <Table dataSource={data} />
  ) : (
    <Tiles list={data} />
  );
});

/**
 * @memberof EvPorts
 * @component
 * @desc EvPorts Controller.
 * @requires EvPortsContext
 */

const EvPortsList = () => {
  const [layoutType, setLayoutType] = useState(eLayoutType.TABLE);
  const [evPorts, setEvPorts] = useState<Api.EvPortDto[]>();
  const [selectedEvPorts, setSelectedEvPorts] = useState<string[]>([]);
  const [query, setQuery] = useState<Api.GetEvPortsQuery>();
  const [limit, setLimit] = useState<number>();
  const [pagination, setPagination] = useState<Api.PaginationSchema>();
  const [loading, setLoading] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean | undefined>();
  //const [openAddDialog, setOpenAddDialog] = useState<boolean | undefined>();
  const [editEvPort, setEditEvPort] = useState<Api.EvPortDto>();
  const deleteConfirm = useConfirmDialog("delete");
  const [queryParams] = useQueryParams({
    search: StringParam,
    id: StringParam,
  });
  const { t } = useTranslation();

  const getEvPorts = useCallback(
    async (page?: number) => {
      const currentPage = pagination ? pagination.page : 1;
      setLoading(true);
      try {
        const { data } = await api.evports.getAll(
          limit,
          page || currentPage,
          query
        );
        data.data && setEvPorts(data.data);
        data.meta && setPagination(data.meta.pagination);
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    },
    [pagination, query, limit]
  );

  const onCreate = useCallback(() => {
    setEditEvPort(undefined);
    setOpenEditDialog(true);
  }, []);

  const onEdit = useCallback((data: Api.EvPortDto) => {
    setEditEvPort(data);
    setOpenEditDialog(true);
  }, []);

  const onUpdateItem = useCallback((updatedPort: Api.EvPortDto) => {
    setEvPorts((prev) => (prev ? updateArrayItem(prev, updatedPort, "id") : []));
  }, []);

  const onUpdate = useCallback(
    async (id: string, formData: Api.UpdateEvPort) => {
      try {
        const {
          data: { data: evPort },
        } = await api.evports.updateEvPort(id, formData);
        onUpdateItem(evPort);
        toast.success(t("text.recordSuccessfullyUpdated"));
      } catch (e) {
        console.error(e);
      }
    },
    [evPorts, t]
  );

  const onDelete = useCallback(
    async (evPort: Api.EvPortDto) => {
      try {
        await deleteConfirm(evPort?.id);
        await api.evports.deleteEvPort(evPort.id);
        getEvPorts();
        toast.success(t("text.recordSuccessfullyDeleted"));
      } catch (e) {
        console.log(e);
        toast.warning(t("text.canceledDelete"));
      }
    },
    [t, getEvPorts]
  );

  const onDeleteMany = useCallback(async () => {
    await deleteConfirm(t("text.records", { count: selectedEvPorts?.length }));
    await api.evports.deleteEvPorts(selectedEvPorts);
    getEvPorts();
    toast.success(t("text.recordsSuccessfullyDeleted"));
    setSelectedEvPorts([]);
  }, [t, selectedEvPorts]);

  const onChangePagination = (page: number) => {
    getEvPorts(page);
  };

  const onSort = useCallback(
    (field: string, order: "desc" | "asc") => {
      setQuery({ ...query, sort: getSortStr(field, order) });
    },
    [query]
  );

  const contextValue = React.useMemo(
    () => ({
      onSort,
      onSelect: setSelectedEvPorts,
      onUpdateItem,
      onEdit,
      onUpdate,
      onDelete,
    }),
    [evPorts]
  );

  const onCloseEditDialog = (result?: boolean) => {
    setOpenEditDialog(false);
    setEditEvPort(undefined);
    result && pagination && getEvPorts(pagination.page);
  };

  /*
  const onCloseAddDialog = (result?: boolean) => {
    setOpenAddDialog(false);
    setEditEvPort(undefined);
    result && pagination && getEvPorts(pagination.page);
  };
  */

  useEffect(() => {
    setQuery(queryParams as Api.GetEvPortsQuery);
  }, [queryParams]);

  useEffect(() => {
    setSelectedEvPorts([]);
  }, [layoutType]);

  useEffect(() => {
    query && getEvPorts();
  }, [query, limit]);

  return (
    <EvPortsContext.Provider value={contextValue}>
      <Box pr={2}>
        <Section
          title={
            <>
              <Typography variant="h4">
                {t("title.evPorts")}{" "}
                <Typography variant="h4" component="span" color="textSecondary">
                  {pagination && pagination.totalCount}
                </Typography>
              </Typography>
            </>
          }
          extra={
            <ButtonGroup>
              <CanIUse permissions={PERMISSION.DELETE_EV_PORT}>
                <Button
                  hidden={!selectedEvPorts.length}
                  prefixIcon={<Icon name="Delete" />}
                  variant="contained"
                  onClick={onDeleteMany}
                >
                  {t("button.delete")}
                </Button>
              </CanIUse>
              <CanIUse permissions={PERMISSION.CREATE_EV_PORT}>
                <Button
                  prefixIcon={<Icon name="Plus" />}
                  variant="contained"
                  onClick={onCreate}
                >
                  {t("button.createNew")}
                </Button>
              </CanIUse>
              <ListLayoutSwitcher
                defaultValue={layoutType}
                onChange={setLayoutType}
              />
            </ButtonGroup>
          }
        />
        <LinearProgress hidden={!loading} />
      </Box>
      <Box flexGrow={1} overflow="auto" mr={2}>
        <List type={layoutType} data={evPorts} />
      </Box>
      <Box pr={2}>
        <Divider />
        {pagination && (
          <Pagination
            prefContent={
              <RowsPerPage initialValue={limit} onChange={setLimit} />
            }
            count={pagination.totalPages}
            page={pagination.page}
            defaultPage={1}
            onChange={(e, page) => {
              onChangePagination(page);
            }}
          />
        )}
      </Box>
      <UpdateEvPortDialog
        evPort={editEvPort}
        open={openEditDialog}
        onClose={onCloseEditDialog}
      />
    </EvPortsContext.Provider>
  );
};

export default EvPortsList;
